import { KeyDisplayValue, StemType } from '@arpeggi/shared'
import { toJS } from 'mobx'
import { getAudioData, getStemAudioData } from '../../stores/song/sample/SampleUtils'
import { ISampleWithEffectsStore } from '../../stores/song/sample/SampleWithEffectsStore'
import { IInstrumentStemStore } from '../../stores/song/stem/InstrumentStemStore'
import { IStemStore } from '../../stores/song/StemStore'
import { AudioEngine } from './AudioEngine'

/**
 * Contains utilities for managing adding Samples to the Audio Engine
 */

export function getSamplePath(sampleWithEffects: ISampleWithEffectsStore) {
  return `${sampleWithEffects.sampleIndex}`
}

/** Returns sampleMap and the audioBuffer */
export function prepareSample(
  sampleWithEffects: any,
  stem: IStemStore
): {
  sampleMap: { path: string; audioDuration: number; sampleWithEffects: any }
  audioBuffer: AudioBuffer
} {
  let sampleMap: any
  const audioData =
    sampleWithEffects.mediaType === 'stem'
      ? getStemAudioData(sampleWithEffects.sampleIndex)
      : getAudioData(sampleWithEffects.sampleIndex)
  if (audioData === null || audioData === undefined) {
    // Don't panic, this just means the sample hasn't downloaded yet.
    // It'll download in a few seconds and the audiograph will re-render.
    sampleMap = { path: '1', audioDuration: 0, sampleWithEffects: {} }
  } else {
    if (stem.type() === StemType.INSTRUMENT) {
      sampleWithEffects = toJS((stem as IInstrumentStemStore).melodicSample)
    }
    const samplePath = getSamplePath(sampleWithEffects)
    sampleMap = { path: samplePath, audioDuration: audioData.audioData.duration, sampleWithEffects }
    // renderer.updateVirtualFileSystem({ [samplePath]: audioData.audioData.getChannelData(0) })
  }

  return { sampleMap, audioBuffer: audioData?.audioData }
}

export function createSampleMap(samples: any, audioEngine: AudioEngine) {
  const sampleMap = []
  samples.map((sampleWithEffects) => {
    const audioData = getAudioData(sampleWithEffects.sampleIndex)
    if (audioData === null || audioData === undefined) {
      // Don't panic, this just means the sample hasn't downloaded yet. It'll download in a few seconds and the audiograph will re-render.
      sampleMap.push('1')
    } else {
      const samplePath = getSamplePath(sampleWithEffects)
      sampleMap.push(samplePath)
    }
  })
  return sampleMap
}

/**
 * clones an audio buffer
 * @param fromAudioBuffer the audio buffer to clone
 * @returns the cloned audio buffer
 */
export function cloneAudioBuffer(fromAudioBuffer: AudioBuffer): AudioBuffer {
  const audioBuffer = new AudioBuffer({
    length: fromAudioBuffer.length,
    numberOfChannels: fromAudioBuffer.numberOfChannels,
    sampleRate: fromAudioBuffer.sampleRate
  })
  for (let channelI = 0; channelI < audioBuffer.numberOfChannels; ++channelI) {
    const samples = fromAudioBuffer.getChannelData(channelI)
    audioBuffer.copyToChannel(samples, channelI)
  }
  return audioBuffer
}

/** Returns the letter note for a given pitch frequency (in hz) */
export const noteFromPitch = (frequencyInHz: number): KeyDisplayValue => {
  const noteStrings: KeyDisplayValue[] = [
    'C',
    'C#',
    'D',
    'D#',
    'E',
    'F',
    'F#',
    'G',
    'G#',
    'A',
    'A#',
    'B'
  ]
  const noteNum = 12 * (Math.log(frequencyInHz / 440) / Math.log(2))
  const index = Math.round(noteNum) + 69
  return noteStrings[index % 12]
}
